// Hasta çalışma alanı editörlerinin (Tedavi, Anamnez, Tetkik) ortak düz dili.
// Kutusuz: her bölüm aynı iç boşlukta, bölümleri ince çizgi ayırır; öğe
// listeleri satır satır ve sabit yükseklikte. Kurallar: docs/design-principles.md §8.3

import React from "react";
import { Box, Button, Tooltip, Typography, alpha } from "@mui/material";
import {
  Person as PersonIcon,
  AccessTime as ClockIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { formatDistanceToNowStrict } from "date-fns";
import { tr } from "date-fns/locale";

import ActionButton from "../../atoms/ActionButton";
import EmptyState from "../../atoms/EmptyState";
import SaveStatusIndicator from "../../atoms/SaveStatusIndicator";
import { COLORS, TYPO } from "../../../styles/uiConstants";

// Yatay boşluk; sağda başlık şeridi hizasına (32px) uzanır.
export const EDITOR_PX = { pl: { xs: 2, md: 3 }, pr: { xs: 2, md: 4 } };

export const SECTION_SX = {
  ...EDITOR_PX,
  py: 2,
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
  borderBottom: `1px solid ${COLORS.slate100}`,
};

export const SECTION_HEADING_SX = {
  fontSize: TYPO.sectionTitle,
  fontWeight: 600,
  color: COLORS.slate700,
};

// İlgili bölümleri bir arada tutan kutusuz grup; başlığı küçük bir etiket.
export const GROUP_BOX_SX = { display: "flex", flexDirection: "column" };

export const GROUP_TITLE_SX = {
  ...EDITOR_PX,
  pt: 2.5,
  pb: 0.25,
  fontSize: TYPO.caption,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.6,
};

// Yan yana iki bölüm (geniş ekranda): ortada tek çizgi, iç boşluk daralır.
export const SPLIT_GRID_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
};
export const SPLIT_LEFT_SX = { ...SECTION_SX, pr: { xs: 2, md: 4, lg: 2 } };
export const SPLIT_RIGHT_SX = {
  ...SECTION_SX,
  pl: { xs: 2, md: 3, lg: 2 },
  borderLeft: { lg: `1px solid ${COLORS.slate100}` },
};

// Öğe listeleri sabit yükseklikte: içerik arttıkça bölüm uzamaz, alan içinde
// kayar. Boşken kaydırma kabı değil: tekerlek sayfaya geçer, çizim ortada durur.
export const listAreaSx = (height, empty = false) => ({
  height,
  overflowY: empty ? "visible" : "auto",
  justifyContent: empty ? "center" : "flex-start",
  overscrollBehavior: empty ? "auto" : "contain",
  display: "flex",
  flexDirection: "column",
  borderTop: `1px solid ${COLORS.slate100}`,
});

export const ITEM_ROW_SX = {
  py: 1,
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  borderBottom: `1px solid ${COLORS.slate100}`,
  "&:last-of-type": { borderBottom: "none" },
};

export const ITEM_NAME_SX = {
  fontWeight: 500,
  fontSize: TYPO.body,
  color: COLORS.slate800,
};

// Satır başındaki kod rozeti (ICD kodu vb.)
export const codeBadgeSx = (color, bg) => ({
  minWidth: 52,
  flexShrink: 0,
  px: 0.75,
  py: 0.25,
  borderRadius: 1,
  textAlign: "center",
  fontSize: TYPO.label,
  fontWeight: 600,
  fontVariantNumeric: "tabular-nums",
  color,
  bgcolor: bg,
});

/** Etiket solda, alan sağda — bölüm hizasında (InfoRow kendi iç boşluğunu taşır). */
export function FieldRow({ label, children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
      <Typography
        sx={{
          minWidth: 110,
          flexShrink: 0,
          pt: 0.6,
          fontSize: TYPO.label,
          fontWeight: 600,
          color: COLORS.slate500,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

/**
 * Bölüm başlığı: ad + soluk sayı, sağda "Ekle" (drawer açar).
 * `addVariant="edit"` mevcut değeri değiştiren butonlar içindir ("Değiştir").
 */
export function SectionHeader({ title, count, addLabel, onAdd, disabled, palette, addVariant = "add" }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        minHeight: 32,
      }}
    >
      <Typography sx={SECTION_HEADING_SX}>
        {title}
        {count ? (
          <Box component="span" sx={{ color: COLORS.slate400, fontWeight: 500 }}>
            {` · ${count}`}
          </Box>
        ) : null}
      </Typography>
      {onAdd ? (
        <ActionButton
          variant={addVariant}
          compact
          palette={palette}
          label={addLabel}
          onClick={onAdd}
          disabled={disabled}
        />
      ) : null}
    </Box>
  );
}

/** Bölüm içi boş durum — standart çizimli (illustrated-sm). */
export function SectionEmpty({ icon, title, hint }) {
  return (
    <EmptyState
      variant="illustrated-sm"
      icon={icon}
      title={title}
      description={hint || undefined}
      sx={{ py: 0 }}
    />
  );
}

const ROLE_LABEL = {
  Admin: "Yönetici",
  AdminStaff: "Admin Personeli",
  ClinicStaff: "Klinik Personeli",
};

const formatEditor = (user) => {
  if (!user) return null;
  const title = user.title ? `${user.title} ` : "";
  const name = `${title}${user.name || ""} ${user.surname || ""}`.trim();
  return { name: name || "—", role: ROLE_LABEL[user.role] || "" };
};

const formatRelativeTr = (value) => {
  if (!value) return "";
  try {
    return formatDistanceToNowStrict(new Date(value), { addSuffix: true, locale: tr });
  } catch (_) {
    return "";
  }
};

/**
 * Editör başlık satırı: solda son düzenleyen + zaman (yeni kayıtta açıklama),
 * sağda otomatik kayıt rozeti ve çağıranın aksiyonları (PDF, durum seçici …).
 *
 * @param {object} [record]
 * @param {string} newLabel - Yeni kayıtta gösterilen metin
 * @param {string} editLabel - Düzenleyen bilgisi yoksa gösterilen metin
 * @param {"idle"|"saving"|"saved"|"error"} [saveStatus]
 * @param {object} [palette]
 * @param {React.ReactNode} [actions]
 */
export function EditorHeader({ record, newLabel, editLabel, saveStatus, palette, actions }) {
  const isNew = !record?._id;
  const editor = formatEditor(record?.updatedBy || record?.createdBy);
  const editedAt = formatRelativeTr(record?.updatedAt || record?.createdAt);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        ...EDITOR_PX,
        py: 1.25,
        borderBottom: `1px solid ${COLORS.slate100}`,
        minHeight: 56,
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flexWrap: "wrap" }}>
        {isNew || !editor ? (
          <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate500 }}>
            {isNew ? newLabel : editLabel}
          </Typography>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <PersonIcon sx={{ fontSize: 16, color: COLORS.slate500 }} />
              <Typography noWrap sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate700 }}>
                {editor.name}
              </Typography>
              {editor.role ? (
                <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate400, ml: 0.5 }}>
                  {editor.role}
                </Typography>
              ) : null}
            </Box>
            {editedAt ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, color: COLORS.slate500 }}>
                <ClockIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: TYPO.label }}>{editedAt}</Typography>
              </Box>
            ) : null}
          </>
        )}
      </Box>
      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        {saveStatus ? <SaveStatusIndicator status={saveStatus} palette={palette} inline /> : null}
        {actions}
      </Box>
    </Box>
  );
}

/** Başlık satırındaki "PDF Çıktısı" butonu (salt-okunur kullanıcı da alabilir). */
export function PdfButton({ onClick, title = "PDF çıktısı", palette }) {
  const tone = palette?.primary || COLORS.slate700;
  return (
    <Tooltip title={title}>
      <Button
        onClick={onClick}
        startIcon={<PdfIcon sx={{ fontSize: 18 }} />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8125rem",
          borderRadius: 999,
          px: 1.75,
          py: 0.6,
          flexShrink: 0,
          color: COLORS.slate600,
          border: `1px solid ${COLORS.slate200}`,
          bgcolor: COLORS.white,
          "&:hover": { borderColor: tone, color: tone, bgcolor: alpha(tone, 0.04) },
        }}
      >
        PDF Çıktısı
      </Button>
    </Tooltip>
  );
}
